import {useQuery} from '@apollo/react-hooks';
import {ApolloError} from 'apollo-client';
import {useMemo} from 'react';
import {v4 as uuidv4} from 'uuid';

import {
  NodeAlertFragmentFragment,
  RunIssueQueryQuery,
} from '../../../generated/graphql';
import {RunIssueQuery} from '../../../state/issues/issues.query';
import {Issue} from '../../../state/issues/types';
import {useRampFlagCoreweaveIssues} from '../../../state/issues/useCoreweaveIssues';
import {propagateErrorsContext} from '../../../util/errors';

type UseRunIssuesOpts = {
  ranOnCoreweave: boolean;
  entityName: string;
  projectName: string;
  runName?: string;
};

function convertNodeAlertToIssue(
  nodeAlert: NodeAlertFragmentFragment,
  runName: string
): Issue {
  // make sure the ids don't collide in the UI
  const id = `${nodeAlert.alertName}-${uuidv4().slice(0, 8)}`;

  return {
    ...nodeAlert,
    cluster: nodeAlert.cluster,
    description: nodeAlert.description,
    grafanaDashboard: nodeAlert.grafanaDashboard,
    id,
    labels: nodeAlert.labels,
    linkURL: nodeAlert.alertName,
    runId: runName ?? '',
    timestamp: parseInt(Object.keys(nodeAlert.occurrences)[0]),
    title: nodeAlert.alertName,
  };
}

export function useRunIssues({
  ranOnCoreweave = false,
  entityName,
  projectName,
  runName,
}: UseRunIssuesOpts): {
  data: Issue[];
  loading: boolean;
  error?: ApolloError;
} {
  const canQueryIssues = useRampFlagCoreweaveIssues();
  const {data, loading, error} = useQuery<RunIssueQueryQuery>(RunIssueQuery, {
    variables: {
      entityName,
      projectName,
      runName,
      start: new Date(0).toISOString(),
    },
    skip: !canQueryIssues || !runName || !ranOnCoreweave,
    context: propagateErrorsContext(),
  });

  return useMemo(
    () => ({
      data:
        data?.project?.run?.nodeAlertsHistory.map(issue =>
          convertNodeAlertToIssue(issue, runName ?? '')
        ) ?? [],
      loading,
      error,
    }),
    [data, loading, error, runName]
  );
}
